import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSession, getSessionScreenshots, getImageUrl } from '../../services/api';
import socketService from '../../services/socket';
import { shouldUseMobileUI } from '../../utils/deviceDetection';
import MobileNav from '../Mobile/MobileNav';
import './MapDisplay.css';

function MapDisplay({ user }) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [session, setSession] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [loadedImages, setLoadedImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Mobile UI state
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile] = useState(shouldUseMobileUI());

  useEffect(() => {
    loadData();

    // Listen for new screenshots
    const handleScreenshotUploaded = () => {
      loadData();
    };

    socketService.onScreenshotUploaded(handleScreenshotUploaded);

    return () => {
      socketService.off('screenshot-uploaded', handleScreenshotUploaded);
    };
  }, [sessionId]);

  useEffect(() => {
    if (screenshots.length > 0 && canvasRef.current) {
      renderMap();
    }
  }, [screenshots, zoom, pan, loadedImages]);

  // Load screenshot images
  useEffect(() => {
    screenshots.forEach((screenshot) => {
      if (!loadedImages[screenshot.id]) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          console.log('[MapDisplay] Image loaded successfully:', screenshot.filePath);
          setLoadedImages((prev) => ({
            ...prev,
            [screenshot.id]: img,
          }));
        };
        img.onerror = (err) => {
          console.error('[MapDisplay] Failed to load image:', {
            screenshotId: screenshot.id,
            filePath: screenshot.filePath,
            fullUrl: getImageUrl(screenshot.filePath),
            error: err
          });
        };
        const imageUrl = getImageUrl(screenshot.filePath);
        console.log('[MapDisplay] Loading image:', imageUrl);
        img.src = imageUrl;
      }
    });
  }, [screenshots]);

  const loadData = async () => {
    try {
      const [sessionData, screenshotsData] = await Promise.all([
        getSession(sessionId),
        getSessionScreenshots(sessionId),
      ]);

      setSession(sessionData);
      setScreenshots(screenshotsData.screenshots || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderMap = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.fillStyle = '#0f1419';
    ctx.fillRect(0, 0, width, height);

    // Draw grid
    ctx.strokeStyle = '#2d3741';
    ctx.lineWidth = 1;

    const gridSize = 50 * zoom;
    const offsetX = pan.x % gridSize;
    const offsetY = pan.y % gridSize;

    for (let x = offsetX; x < width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    for (let y = offsetY; y < height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Draw screenshots
    const screenshotSize = 200 * zoom;
    const gap = 20 * zoom;
    const cols = Math.floor(width / (screenshotSize + gap));

    screenshots.forEach((screenshot, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);

      const x = col * (screenshotSize + gap) + gap + pan.x;
      const y = row * (screenshotSize + gap) + gap + pan.y;

      const img = loadedImages[screenshot.id];
      if (img) {
        // Draw the actual screenshot image
        const imgAspect = img.width / img.height;
        const imgWidth = screenshotSize;
        const imgHeight = screenshotSize / imgAspect;
        
        ctx.drawImage(img, x, y, imgWidth, imgHeight);
        
        // Draw border
        ctx.strokeStyle = '#1da1f2';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, imgWidth, imgHeight);
        
        // Draw label below image
        ctx.fillStyle = '#e1e8ed';
        ctx.font = `${12 * zoom}px sans-serif`;
        ctx.fillText(
          screenshot.username || screenshot.displayName,
          x + 10,
          y + imgHeight + 20 * zoom
        );
      } else {
        // Draw placeholder while loading
        const placeholderHeight = screenshotSize * 0.6;
        ctx.fillStyle = '#1a2332';
        ctx.fillRect(x, y, screenshotSize, placeholderHeight);

        // Draw border
        ctx.strokeStyle = '#1da1f2';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, screenshotSize, placeholderHeight);

        // Loading text
        ctx.fillStyle = '#e1e8ed';
        ctx.font = `${12 * zoom}px sans-serif`;
        ctx.fillText('Loading...', x + 10, y + placeholderHeight / 2);
      }
    });
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((prev) => Math.max(0.5, Math.min(3, prev * delta)));
  };

  if (loading) {
    return (
      <div className="map-display loading">
        <div className="loading-spinner"></div>
        <p>Loading map...</p>
      </div>
    );
  }

  return (
    <div className="map-display">
      {/* Mobile Navigation */}
      {isMobile && (
        <MobileNav
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          user={user}
          onLogout={() => navigate('/')}
        />
      )}

      <header className="map-header">
        <button className="btn btn-secondary" onClick={() => navigate(`/session/${sessionId}`)}>
          ← Back to Session
        </button>
        <h1>Tactical Map</h1>
        <div className="map-controls">
          {isMobile ? (
            <button 
              className="btn-hamburger" 
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open menu"
            >
              ☰
            </button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setZoom(zoom * 1.2)}>
                +
              </button>
              <span>{Math.round(zoom * 100)}%</span>
              <button className="btn btn-secondary" onClick={() => setZoom(zoom * 0.8)}>
                -
              </button>
              <button className="btn btn-secondary" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}>
                Reset
              </button>
            </>
          )}
        </div>
      </header>

      <div className="map-container">
        <canvas
          ref={canvasRef}
          width={window.innerWidth}
          height={window.innerHeight - 80}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        />
      </div>

      <div className="map-legend">
        <h3>Legend</h3>
        <div className="legend-item">
          <div className="legend-icon" style={{ backgroundColor: '#1da1f2' }}></div>
          <span>Screenshot Location</span>
        </div>
        <div className="legend-item">
          <div className="legend-icon" style={{ backgroundColor: '#17bf63' }}></div>
          <span>Your Position</span>
        </div>
      </div>
    </div>
  );
}

export default MapDisplay;

